import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Mail, Inbox, Send, Trash2, RefreshCw, Search, ChevronLeft, Eye, Clock, User, Reply, Paperclip, Plus, Folder } from 'lucide-react';
import { toast } from 'sonner';
import { api } from '../App';
import Navbar from '../components/Navbar';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';

const AdminEmailsPage = () => {
  const [activeTab, setActiveTab] = useState('mailbox');
  const [mailboxEmails, setMailboxEmails] = useState([]);
  const [sentEmails, setSentEmails] = useState([]);
  const [contactMessages, setContactMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEmail, setSelectedEmail] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [unreadCount, setUnreadCount] = useState(0);
  const [currentFolder, setCurrentFolder] = useState('INBOX');
  const [folders, setFolders] = useState(['INBOX']);
  
  // Compose modal
  const [composeModal, setComposeModal] = useState(false);
  const [composeTo, setComposeTo] = useState('');
  const [composeSubject, setComposeSubject] = useState('');
  const [composeMessage, setComposeMessage] = useState('');
  const [sending, setSending] = useState(false);
  
  // Reply modal
  const [replyModal, setReplyModal] = useState({ open: false, email: null });
  const [replyMessage, setReplyMessage] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [mailboxRes, sentRes, contactRes, foldersRes, unreadRes] = await Promise.all([
        api.get('/admin/mailbox/emails?limit=100'),
        api.get('/admin/emails/sent'),
        api.get('/admin/emails/inbox'),
        api.get('/admin/mailbox/folders').catch(() => ({ data: { folders: ['INBOX'] } })),
        api.get('/admin/mailbox/unread-count').catch(() => ({ data: { unread: 0 } }))
      ]);
      setMailboxEmails(mailboxRes.data.emails || []);
      setSentEmails(sentRes.data.emails || []);
      setContactMessages(contactRes.data.messages || []);
      setFolders(foldersRes.data.folders || ['INBOX']);
      setUnreadCount(unreadRes.data.unread || 0);
    } catch (err) {
      console.error('Error fetching emails:', err);
      toast.error('Failed to load emails');
    } finally {
      setLoading(false);
    }
  };

  const openMailboxEmail = async (email) => {
    try {
      const res = await api.get(`/admin/mailbox/emails/${email.id}?folder=${currentFolder}`);
      setSelectedEmail({ ...res.data, type: 'mailbox' });
      // Mark as read
      if (!email.is_read) {
        await api.post(`/admin/mailbox/emails/${email.id}/read?folder=${currentFolder}`);
        fetchData();
      }
    } catch (err) {
      toast.error('Failed to load email');
    }
  };

  const openContactMessage = async (msg) => {
    try {
      const res = await api.get(`/admin/emails/${msg.id}`);
      setSelectedEmail({ ...res.data, type: 'contact' });
    } catch (err) {
      toast.error('Failed to load message');
    }
  };

  const deleteMailboxEmail = async (emailId) => {
    if (!confirm('Are you sure you want to delete this email?')) return;
    try {
      await api.delete(`/admin/mailbox/emails/${emailId}?folder=${currentFolder}`);
      toast.success('Email deleted');
      setSelectedEmail(null);
      fetchData();
    } catch (err) {
      toast.error('Failed to delete email');
    }
  };

  const sendEmail = async () => {
    if (!composeTo || !composeSubject || !composeMessage) {
      toast.error('Please fill all fields');
      return;
    }
    
    setSending(true);
    try {
      const res = await api.post('/admin/mailbox/send', {
        to_email: composeTo,
        subject: composeSubject,
        body: composeMessage
      });
      
      if (res.data.success) {
        toast.success('Email sent successfully!');
        setComposeModal(false);
        setComposeTo('');
        setComposeSubject('');
        setComposeMessage('');
        fetchData();
      } else {
        toast.error(res.data.message || 'Failed to send email');
      }
    } catch (err) {
      toast.error('Failed to send email');
    } finally {
      setSending(false);
    }
  };

  const sendReply = async () => {
    if (!replyMessage) {
      toast.error('Please enter a message');
      return;
    }
    
    const email = replyModal.email;
    setSending(true);
    try {
      let res;
      
      // Use different endpoint for contact form messages vs mailbox emails
      if (email.type === 'contact') {
        // Reply to contact form submission - updates status to 'replied'
        res = await api.post(`/admin/emails/${email.id}/reply`, {
          to_email: email.email,
          subject: `Re: ${email.subject}`,
          message: replyMessage
        });
      } else {
        // Reply to real mailbox email
        res = await api.post('/admin/mailbox/reply', {
          to_email: email.from_email || email.email,
          subject: email.subject,
          body: replyMessage,
          original_message_id: email.message_id || null
        });
      }
      
      if (res.data.success) {
        toast.success('Reply sent successfully!');
        setReplyModal({ open: false, email: null });
        setReplyMessage('');
        fetchData();
      } else {
        toast.error(res.data.message || 'Failed to send reply');
      }
    } catch (err) {
      toast.error('Failed to send reply');
    } finally {
      setSending(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    const now = new Date();
    const diff = now - date;
    
    if (diff < 86400000) { // Less than 24 hours
      return date.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit' });
    } else if (diff < 604800000) { // Less than 7 days
      return date.toLocaleDateString('en-IN', { weekday: 'short' });
    }
    return date.toLocaleDateString('en-IN', { day: 'numeric', month: 'short' });
  };

  const filteredMailboxEmails = mailboxEmails.filter(email => 
    (email.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.from_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    email.from_email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const filteredContactMessages = contactMessages.filter(msg =>
    (msg.name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.subject?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    msg.email?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  return (
    <div className="min-h-screen bg-slate-50">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 py-8">
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Email Management</h1>
            <p className="text-slate-500">Manage your mailbox - info@legalhubs.info</p>
          </div>
          <div className="flex gap-2">
            <Button 
              onClick={() => setComposeModal(true)}
              className="bg-amber-600 hover:bg-amber-700"
              data-testid="compose-email-btn"
            >
              <Plus className="w-4 h-4 mr-2" />
              Compose
            </Button>
            <Button 
              variant="outline" 
              onClick={fetchData}
              disabled={loading}
              data-testid="refresh-emails-btn"
            >
              <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </Button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Inbox className="w-5 h-5 text-blue-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{unreadCount}</p>
                <p className="text-xs text-slate-500">Unread</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Mail className="w-5 h-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{mailboxEmails.length}</p>
                <p className="text-xs text-slate-500">Total Inbox</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-amber-100 rounded-lg">
                <Send className="w-5 h-5 text-amber-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{sentEmails.length}</p>
                <p className="text-xs text-slate-500">Sent</p>
              </div>
            </div>
          </div>
          <div className="bg-white rounded-xl p-4 border border-slate-200">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-purple-100 rounded-lg">
                <User className="w-5 h-5 text-purple-600" />
              </div>
              <div>
                <p className="text-2xl font-bold text-slate-900">{contactMessages.length}</p>
                <p className="text-xs text-slate-500">Contact Forms</p>
              </div>
            </div>
          </div>
        </div>

        {/* Search */}
        <div className="relative mb-6">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
          <Input
            placeholder="Search emails..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="pl-10"
            data-testid="email-search-input"
          />
        </div>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="mb-4">
            <TabsTrigger value="mailbox" className="flex items-center gap-2">
              <Inbox className="w-4 h-4" />
              Mailbox
              {unreadCount > 0 && (
                <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">{unreadCount}</span>
              )}
            </TabsTrigger>
            <TabsTrigger value="sent" className="flex items-center gap-2">
              <Send className="w-4 h-4" />
              Sent
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center gap-2">
              <User className="w-4 h-4" />
              Contact Forms
            </TabsTrigger>
          </TabsList>

          {/* Mailbox Tab */}
          <TabsContent value="mailbox">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {loading ? (
                <div className="p-8 text-center text-slate-500">Loading...</div>
              ) : filteredMailboxEmails.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Mail className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No emails found</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredMailboxEmails.map((email, idx) => (
                    <motion.div
                      key={email.id || idx}
                      initial={{ opacity: 0 }}
                      animate={{ opacity: 1 }}
                      className={`p-4 hover:bg-slate-50 cursor-pointer transition-colors ${!email.is_read ? 'bg-blue-50/50' : ''}`}
                      onClick={() => openMailboxEmail(email)}
                      data-testid={`mailbox-email-${idx}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${!email.is_read ? 'bg-blue-600' : 'bg-slate-400'}`}>
                          {email.from_name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center justify-between">
                            <p className={`font-medium truncate ${!email.is_read ? 'text-slate-900' : 'text-slate-600'}`}>
                              {email.from_name || email.from_email}
                            </p>
                            <span className="text-xs text-slate-400 whitespace-nowrap ml-2">
                              {formatDate(email.date)}
                            </span>
                          </div>
                          <p className={`text-sm truncate ${!email.is_read ? 'font-medium text-slate-800' : 'text-slate-600'}`}>
                            {email.subject || '(No Subject)'}
                          </p>
                          <p className="text-xs text-slate-400 truncate mt-1">
                            {email.preview || email.body?.substring(0, 100)}
                          </p>
                          {email.has_attachments && (
                            <div className="flex items-center gap-1 mt-1 text-xs text-slate-400">
                              <Paperclip className="w-3 h-3" />
                              Attachment
                            </div>
                          )}
                        </div>
                        {!email.is_read && (
                          <div className="w-2 h-2 bg-blue-600 rounded-full"></div>
                        )}
                      </div>
                    </motion.div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Sent Tab */}
          <TabsContent value="sent">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {sentEmails.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <Send className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No sent emails</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {sentEmails.map((email, idx) => (
                    <div
                      key={email.id || idx}
                      className="p-4 hover:bg-slate-50"
                      data-testid={`sent-email-${idx}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Send className="w-4 h-4 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-900">To: {email.to_email}</p>
                            <p className="text-sm text-slate-600">{email.subject}</p>
                          </div>
                        </div>
                        <div className="text-right">
                          <span className={`text-xs px-2 py-1 rounded-full ${
                            email.status === 'sent' ? 'bg-emerald-100 text-emerald-700' : 
                            email.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-slate-100 text-slate-600'
                          }`}>
                            {email.status}
                          </span>
                          <p className="text-xs text-slate-400 mt-1">{formatDate(email.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>

          {/* Contact Forms Tab */}
          <TabsContent value="contacts">
            <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
              {filteredContactMessages.length === 0 ? (
                <div className="p-8 text-center text-slate-500">
                  <User className="w-12 h-12 mx-auto mb-4 text-slate-300" />
                  <p>No contact form submissions</p>
                </div>
              ) : (
                <div className="divide-y divide-slate-100">
                  {filteredContactMessages.map((msg, idx) => (
                    <div
                      key={msg.id || idx}
                      className={`p-4 hover:bg-slate-50 cursor-pointer ${msg.status === 'new' ? 'bg-amber-50/50' : ''}`}
                      onClick={() => openContactMessage(msg)}
                      data-testid={`contact-msg-${idx}`}
                    >
                      <div className="flex items-start gap-4">
                        <div className={`w-10 h-10 rounded-full flex items-center justify-center text-white font-medium ${msg.status === 'new' ? 'bg-amber-600' : 'bg-slate-400'}`}>
                          {msg.name?.charAt(0)?.toUpperCase() || 'U'}
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <p className="font-medium text-slate-900">{msg.name}</p>
                            <span className={`text-xs px-2 py-1 rounded-full ${
                              msg.status === 'new' ? 'bg-amber-100 text-amber-700' :
                              msg.status === 'replied' ? 'bg-emerald-100 text-emerald-700' :
                              'bg-slate-100 text-slate-600'
                            }`}>
                              {msg.status}
                            </span>
                          </div>
                          <p className="text-sm text-slate-600">{msg.subject}</p>
                          <p className="text-xs text-slate-400">{msg.email}</p>
                          <p className="text-xs text-slate-400 mt-1">{formatDate(msg.created_at)}</p>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </TabsContent>
        </Tabs>

        {/* Email Detail Modal */}
        <Dialog open={!!selectedEmail} onOpenChange={() => setSelectedEmail(null)}>
          <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
            {selectedEmail && (
              <>
                <DialogHeader>
                  <DialogTitle className="text-lg">{selectedEmail.subject || '(No Subject)'}</DialogTitle>
                </DialogHeader>
                
                <div className="space-y-4">
                  <div className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                    <div className="flex items-center gap-3">
                      <div className="w-10 h-10 rounded-full bg-slate-200 flex items-center justify-center text-slate-600 font-medium">
                        {(selectedEmail.from_name || selectedEmail.name)?.charAt(0)?.toUpperCase() || 'U'}
                      </div>
                      <div>
                        <p className="font-medium text-slate-900">
                          {selectedEmail.from_name || selectedEmail.name}
                        </p>
                        <p className="text-sm text-slate-500">
                          {selectedEmail.from_email || selectedEmail.email}
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-slate-500">{formatDate(selectedEmail.date || selectedEmail.created_at)}</p>
                      {selectedEmail.phone && (
                        <p className="text-xs text-slate-400">📞 {selectedEmail.phone}</p>
                      )}
                    </div>
                  </div>

                  <div className="p-4 bg-white border border-slate-200 rounded-lg">
                    <p className="text-slate-700 whitespace-pre-wrap">
                      {selectedEmail.body || selectedEmail.message}
                    </p>
                  </div>

                  {selectedEmail.attachments && selectedEmail.attachments.length > 0 && (
                    <div className="p-3 bg-slate-50 rounded-lg">
                      <p className="text-sm font-medium text-slate-700 mb-2">Attachments:</p>
                      <div className="space-y-1">
                        {selectedEmail.attachments.map((att, idx) => (
                          <div key={idx} className="flex items-center gap-2 text-sm text-slate-600">
                            <Paperclip className="w-4 h-4" />
                            {att.filename} ({Math.round(att.size / 1024)}KB)
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>

                <DialogFooter className="flex gap-2">
                  <Button
                    variant="outline"
                    onClick={() => {
                      setReplyModal({ open: true, email: selectedEmail });
                      setSelectedEmail(null);
                    }}
                    data-testid="reply-email-btn"
                  >
                    <Reply className="w-4 h-4 mr-2" />
                    Reply
                  </Button>
                  {selectedEmail.type === 'mailbox' && (
                    <Button
                      variant="destructive"
                      onClick={() => deleteMailboxEmail(selectedEmail.id)}
                      data-testid="delete-email-btn"
                    >
                      <Trash2 className="w-4 h-4 mr-2" />
                      Delete
                    </Button>
                  )}
                </DialogFooter>
              </>
            )}
          </DialogContent>
        </Dialog>

        {/* Compose Modal */}
        <Dialog open={composeModal} onOpenChange={setComposeModal}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Compose Email</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div>
                <label className="text-sm font-medium text-slate-700">To</label>
                <Input
                  value={composeTo}
                  onChange={(e) => setComposeTo(e.target.value)}
                  placeholder="recipient@example.com"
                  data-testid="compose-to-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Subject</label>
                <Input
                  value={composeSubject}
                  onChange={(e) => setComposeSubject(e.target.value)}
                  placeholder="Email subject"
                  data-testid="compose-subject-input"
                />
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Message</label>
                <Textarea
                  value={composeMessage}
                  onChange={(e) => setComposeMessage(e.target.value)}
                  placeholder="Type your message..."
                  rows={8}
                  data-testid="compose-message-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setComposeModal(false)}>
                Cancel
              </Button>
              <Button 
                onClick={sendEmail}
                disabled={sending}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="send-email-btn"
              >
                {sending ? 'Sending...' : 'Send Email'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Reply Modal */}
        <Dialog open={replyModal.open} onOpenChange={(open) => setReplyModal({ open, email: open ? replyModal.email : null })}>
          <DialogContent className="max-w-xl">
            <DialogHeader>
              <DialogTitle>Reply to {replyModal.email?.from_name || replyModal.email?.name}</DialogTitle>
            </DialogHeader>
            
            <div className="space-y-4">
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm text-slate-500">To: {replyModal.email?.from_email || replyModal.email?.email}</p>
                <p className="text-sm text-slate-500">Subject: Re: {replyModal.email?.subject}</p>
              </div>
              <div>
                <label className="text-sm font-medium text-slate-700">Your Reply</label>
                <Textarea
                  value={replyMessage}
                  onChange={(e) => setReplyMessage(e.target.value)}
                  placeholder="Type your reply..."
                  rows={8}
                  data-testid="reply-message-input"
                />
              </div>
            </div>

            <DialogFooter>
              <Button variant="outline" onClick={() => setReplyModal({ open: false, email: null })}>
                Cancel
              </Button>
              <Button 
                onClick={sendReply}
                disabled={sending}
                className="bg-amber-600 hover:bg-amber-700"
                data-testid="send-reply-btn"
              >
                {sending ? 'Sending...' : 'Send Reply'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default AdminEmailsPage;
